/* ============================================================
   Recipe Template Interactive Features
   - Servings adjuster (multiplies ingredient amounts)
   - US/Metric unit toggle
   - Step timers with audio alert
   - Save/bookmark to localStorage
   ============================================================ */

(function() {
  'use strict';

  document.addEventListener('DOMContentLoaded', function() {
    initServingsAdjuster();
    initUnitToggle();
    initStepTimers();
    initSaveButton();
    initSmoothScroll();
  });

  /* ----------------------------------------------------------
     SERVINGS ADJUSTER
     Updates ingredient amounts proportionally when user
     changes the servings count.
     ---------------------------------------------------------- */
  function initServingsAdjuster() {
    const input = document.querySelector('.servings-input');
    if (!input) return;

    const baseServings = parseInt(input.value, 10) || 4;
    const decreaseBtn = document.querySelector('[data-servings-action="decrease"]');
    const increaseBtn = document.querySelector('[data-servings-action="increase"]');
    const display = document.querySelector('.servings-display');

    function updateAmounts() {
      const newServings = Math.max(1, parseInt(input.value, 10) || 1);
      input.value = newServings;
      const multiplier = newServings / baseServings;

      if (display) display.textContent = newServings;

      document.querySelectorAll('.ingredient-amount').forEach(function(el) {
        const baseAmount = parseFloat(el.dataset.baseAmount);
        if (isNaN(baseAmount)) return;

        const newAmount = baseAmount * multiplier;
        el.textContent = formatAmount(newAmount);
      });
    }

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', function() {
        input.value = Math.max(1, parseInt(input.value, 10) - 1);
        updateAmounts();
      });
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', function() {
        input.value = parseInt(input.value, 10) + 1;
        updateAmounts();
      });
    }

    input.addEventListener('input', updateAmounts);
    input.addEventListener('change', updateAmounts);
  }

  function formatAmount(num) {
    // Convert decimals to fractions for common cooking amounts
    const fractions = {
      0.125: '⅛', 0.25: '¼', 0.333: '⅓', 0.375: '⅜',
      0.5: '½', 0.625: '⅝', 0.667: '⅔', 0.75: '¾', 0.875: '⅞'
    };

    const whole = Math.floor(num);
    const decimal = Math.round((num - whole) * 1000) / 1000;

    // Find closest fraction
    let closestFraction = '';
    let smallestDiff = 0.05;
    for (const dec in fractions) {
      const diff = Math.abs(decimal - parseFloat(dec));
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestFraction = fractions[dec];
      }
    }

    if (closestFraction) {
      return whole > 0 ? whole + ' ' + closestFraction : closestFraction;
    }

    // Otherwise show as decimal with up to 2 places
    return Number(num.toFixed(2)).toString();
  }

  /* ----------------------------------------------------------
     UNIT TOGGLE (US <-> Metric)
     ---------------------------------------------------------- */
  function initUnitToggle() {
    const buttons = document.querySelectorAll('.unit-btn');
    if (buttons.length === 0) return;

    buttons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        const unit = this.dataset.unit;
        buttons.forEach(b => b.classList.remove('active'));
        this.classList.add('active');

        document.querySelectorAll('.ingredient-amount').forEach(function(el) {
          const item = el.closest('.ingredient-item');
          const unitEl = item.querySelector('.ingredient-unit');

          if (unit === 'metric' && el.dataset.metricAmount) {
            el.textContent = el.dataset.metricAmount;
            if (unitEl) unitEl.textContent = el.dataset.metricUnit || '';
          } else {
            el.textContent = formatAmount(parseFloat(el.dataset.baseAmount));
            if (unitEl) unitEl.textContent = el.dataset.usUnit || '';
          }
        });
      });
    });
  }

  /* ----------------------------------------------------------
     STEP TIMERS
     Click to start a countdown for a specific step.
     ---------------------------------------------------------- */
  function initStepTimers() {
    const timers = document.querySelectorAll('.step-timer');

    timers.forEach(function(btn) {
      const minutes = parseFloat(btn.dataset.timerMinutes);
      if (isNaN(minutes)) return;

      let intervalId = null;
      let secondsLeft = minutes * 60;
      const originalText = btn.innerHTML;

      btn.addEventListener('click', function() {
        if (intervalId) {
          // Stop timer
          clearInterval(intervalId);
          intervalId = null;
          btn.innerHTML = originalText;
          btn.classList.remove('running');
          secondsLeft = minutes * 60;
          return;
        }

        // Start timer
        btn.classList.add('running');

        function tick() {
          secondsLeft--;
          if (secondsLeft <= 0) {
            clearInterval(intervalId);
            intervalId = null;
            btn.classList.remove('running');
            btn.innerHTML = '✓ Time\'s up!';
            playAlert();
            setTimeout(function() {
              btn.innerHTML = originalText;
              secondsLeft = minutes * 60;
            }, 5000);
            return;
          }
          const m = Math.floor(secondsLeft / 60);
          const s = secondsLeft % 60;
          btn.innerHTML = '⏱ ' + m + ':' + (s < 10 ? '0' + s : s);
        }

        tick();
        intervalId = setInterval(tick, 1000);
      });
    });
  }

  function playAlert() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);
      osc.start();
      osc.stop(ctx.currentTime + 1);
    } catch (e) { /* silent fail */ }
  }

  /* ----------------------------------------------------------
     SAVE / BOOKMARK
     Persists to localStorage so user can find recipe later.
     ---------------------------------------------------------- */
  function initSaveButton() {
    const btn = document.querySelector('[data-recipe-save]');
    if (!btn) return;

    const recipeUrl = window.location.pathname;
    const recipeTitle = document.querySelector('.recipe-title')?.textContent || document.title;
    const STORAGE_KEY = 'rir_saved_recipes';

    function getSaved() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      } catch (e) {
        return [];
      }
    }

    function isSaved() {
      return getSaved().some(r => r.url === recipeUrl);
    }

    function updateButton() {
      if (isSaved()) {
        btn.classList.add('saved');
        btn.querySelector('span') ? btn.querySelector('span').textContent = 'Saved' : null;
        // Find the text node and update it
        Array.from(btn.childNodes).forEach(node => {
          if (node.nodeType === 3 && node.textContent.trim()) {
            node.textContent = ' Saved';
          }
        });
      } else {
        btn.classList.remove('saved');
        Array.from(btn.childNodes).forEach(node => {
          if (node.nodeType === 3 && node.textContent.trim()) {
            node.textContent = ' Save';
          }
        });
      }
    }

    btn.addEventListener('click', function() {
      const saved = getSaved();
      if (isSaved()) {
        const filtered = saved.filter(r => r.url !== recipeUrl);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      } else {
        saved.push({ url: recipeUrl, title: recipeTitle, savedAt: Date.now() });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
      }
      updateButton();
    });

    updateButton();
  }

  /* ----------------------------------------------------------
     SMOOTH SCROLL for Jump to Recipe
     ---------------------------------------------------------- */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;
        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }
})();
